// mockSurveyPresence.js — live presence for Mock Survey collaboration.
//
// Uses Supabase Realtime Presence (ephemeral — NO database schema/migration).
// One channel per facility carries everyone currently in ANY mock-survey audit
// for that facility. Each participant advertises { userId, name, runId, tag },
// so:
//   • the Command Center can show who is in which audit right now, and
//   • the live session can HIGHLIGHT the tag a teammate is actively working —
//     the advisory that stops two people from overwriting each other's work.
//
// Realtime Presence rides the same websocket the app already uses; it needs no
// table, publication, or RLS change. If Realtime is unreachable the hooks simply
// report an empty participant list and the UI degrades to single-user behavior.

import { useEffect, useRef, useState, useCallback } from "react";

function channelName(facilityId) {
  return `mock-survey:${facilityId}`;
}

// Flatten Supabase presenceState() ({ key: [meta, ...] }) into a flat array.
function flatten(state) {
  const out = [];
  Object.values(state || {}).forEach((metas) => {
    (metas || []).forEach((m) => out.push(m));
  });
  return out;
}

// Low-level shared subscription. Returns { participants, update } where `update`
// re-advertises this client's payload (e.g. when the active tag changes) without
// tearing the channel down.
function usePresenceChannel(facilityId, initialPayload, enabled) {
  const [participants, setParticipants] = useState([]);
  const channelRef = useRef(null);
  const payloadRef = useRef(initialPayload);
  payloadRef.current = initialPayload;

  useEffect(() => {
    if (!enabled || !facilityId) { setParticipants([]); return; }
    let cancelled = false;
    import("./supabaseClient")
      .then(({ supabase }) => {
        if (!supabase || cancelled) return;
        const key = String((initialPayload && initialPayload.userId) || Math.random().toString(36).slice(2));
        const channel = supabase.channel(channelName(facilityId), { config: { presence: { key } } });
        channelRef.current = channel;
        channel.on("presence", { event: "sync" }, () => {
          if (!cancelled) setParticipants(flatten(channel.presenceState()));
        });
        channel.subscribe(async (status) => {
          if (status === "SUBSCRIBED" && payloadRef.current) {
            try { await channel.track({ ...payloadRef.current, at: Date.now() }); } catch (_) { /* ignore */ }
          }
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      const ch = channelRef.current;
      channelRef.current = null;
      if (ch) {
        try { ch.untrack(); } catch (_) { /* ignore */ }
        import("./supabaseClient")
          .then(({ supabase }) => { try { if (supabase) supabase.removeChannel(ch); } catch (_) { /* ignore */ } })
          .catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, enabled]);

  const update = useCallback((payload) => {
    payloadRef.current = payload;
    const ch = channelRef.current;
    if (ch) { try { ch.track({ ...payload, at: Date.now() }); } catch (_) { /* ignore */ } }
  }, []);

  return { participants, update };
}

// ─── Session hook ───────────────────────────────────────────────────────────
// Advertise this user on a specific run + tag; return the OTHER people in the
// same run (excluding self). Re-tracks automatically as the active tag changes.
export function useRunPresence({ facilityId, runId, userId, name, currentTag, enabled = true }) {
  const uid = String(userId || "anon");
  const payload = { userId: uid, name: name || "Someone", runId: String(runId || ""), tab: "session", tag: currentTag || null };
  const { participants, update } = usePresenceChannel(facilityId, payload, enabled && !!runId);

  useEffect(() => {
    update({ userId: uid, name: name || "Someone", runId: String(runId || ""), tab: "session", tag: currentTag || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTag, runId]);

  const others = participants.filter(
    (p) => p.tab === "session" && String(p.runId) === String(runId) && String(p.userId) !== uid
  );
  return { others };
}

// ─── Command Center hook ──────────────────────────────────────────────────────
// Who is currently in each run at this facility. Also advertises a lightweight
// "browsing" presence so teammates can see you're in the Command Center.
export function useFacilityPresence({ facilityId, userId, name, enabled = true }) {
  const payload = { userId: String(userId || "anon"), name: name || "Someone", runId: "", tab: "command-center", tag: null };
  const { participants } = usePresenceChannel(facilityId, payload, enabled);

  const byRun = {};
  participants.forEach((p) => {
    if (p.tab === "session" && p.runId) {
      (byRun[p.runId] = byRun[p.runId] || []).push(p);
    }
  });
  return { byRun, participants };
}

// Small helper: first name (or the whole label if there's no space).
export function firstName(name) {
  return String(name || "Someone").trim().split(/\s+/)[0];
}
